import { UserInputAnswer } from "./node-execution";

/**
 * What the resume says when the user closes the question card without answering.
 * Shared so every surface that can dismiss a parked question set resumes the agent
 * with the same sentence.
 */
export const SURVEY_DECLINED_MESSAGE =
  "I'd rather not answer that — please continue without it.";

/**
 * The two renderings of an answers reply, derived in ONE place because two of
 * them have to agree: the optimistic bubble the chat paints immediately and
 * the server echo that reconciles over it. They drifted once already for
 * choice replies, which is why `text` and `displayText` are kept apart at all.
 *
 * `text` is what the model reads — option `value`s verbatim, so the agent gets
 * back exactly the strings it offered. `displayText` is what the merchant sees
 * in their own transcript — option labels, one line per question.
 */
export function formatUserInputAnswers(answers: UserInputAnswer[]): {
  text: string;
  displayText: string;
} {
  const text = answers
    .map((answer) => {
      const value =
        answer.choices.length > 0
          ? answer.choices.map((choice) => choice.value).join(", ")
          : (answer.freeText ?? "");
      return `Q: ${answer.prompt}\nA: ${value}`;
    })
    .join("\n\n");

  const displayText = answers
    .map((answer) => {
      const value =
        answer.choices.length > 0
          ? answer.choices.map((choice) => choice.label).join(", ")
          : (answer.freeText ?? "");
      return `${answer.prompt} — ${value}`;
    })
    .join("\n");

  return { text, displayText };
}

/**
 * One question of the recap card the transcript renders for an answers reply
 * (Atlas Component Library › "Survey summary row"): the question, the chosen
 * answer, and — for a long set — the itemized picks the row expands to reveal.
 *
 * Render-only, and derived HERE rather than in the UI for the same reason
 * `displayText` is: the optimistic bubble and the server echo both build it,
 * and they have to agree.
 */
export interface SurveySummaryRow {
  prompt: string;
  answer: string;
  /** Present only when `answer` is a count that collapses this list. */
  items?: string[];
}

/**
 * Above this many picks a joined label list stops being readable, so the row
 * collapses to a count ("4 selected") and hands the labels to the expandable
 * list instead.
 */
const COLLAPSE_ANSWER_THRESHOLD = 4;

/**
 * The structured rendering of an answers reply — one row per question. Same
 * inputs as `formatUserInputAnswers`, and the same rule for which side of the
 * value/label split the reader sees: labels, never option values.
 */
export function buildSurveySummary(
  answers: UserInputAnswer[]
): SurveySummaryRow[] {
  return answers.map((answer) => {
    const labels = answer.choices.map((choice) => choice.label);
    if (labels.length >= COLLAPSE_ANSWER_THRESHOLD) {
      return {
        prompt: answer.prompt,
        answer: `${labels.length} selected`,
        items: labels,
      };
    }
    return {
      prompt: answer.prompt,
      answer: labels.length > 0 ? labels.join(", ") : (answer.freeText ?? ""),
    };
  });
}
